import {
  ChargePointTariffListItem,
  ChargePointTariffListItemSkeleton,
  ChargingSpecsAdd,
} from 'Components'
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
  useRef,
} from 'react'

import { EmptySpecs } from 'Components/IFEmptyStates/IFEmptyStates'
import PropTypes from 'prop-types'
import Styles from './TariffList.module.css'
import { Virtuoso } from 'react-virtuoso'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { useOverlayScrollbars } from 'overlayscrollbars-react'
import 'overlayscrollbars/overlayscrollbars.css'
import { getEnergyPriceRange } from 'Utils/Calculations'

const TariffList = forwardRef(
  (
    {
      displayedList = [],
      dropdownList = [],
      isEditing,
      isLoading,
      maxListHeight,
      scrollbarsHeight,
      onSubmit = () => {},
      removeExtraMargin,
      country,
    },
    ref,
  ) => {
    const [deleteTariffs, setDeleteTariffs] = useState([])
    const [addTariffs, setAddTariffs] = useState([])
    const [filteredTariffs, setfilteredTariffs] = useState([])
    const [newTariffs, setNewTariffs] = useState([])
    const [dynamicHeight, setDynamicHeight] = useState(0)
    const [combinedTariffs, setCombinedTariffs] = useState(displayedList)
    const { t } = useTranslation()

    useEffect(() => {
      const errorHandler = (e) => {
        if (
          e.message.includes(
            'ResizeObserver loop completed with undelivered notifications' ||
              'ResizeObserver loop limit exceeded',
          )
        ) {
          const resizeObserverErr = document.getElementById(
            'webpack-dev-server-client-overlay',
          )
          if (resizeObserverErr) {
            resizeObserverErr.style.display = 'none'
          }
        }
      }
      window.addEventListener('error', errorHandler)
      return () => {
        window.removeEventListener('error', errorHandler)
      }
    }, [])

    // Work around for displayedList firing useEffect without it's value changing
    const [newDisplayedList, setNewDisplayedList] = useState(displayedList)
    useEffect(() => {
      if (JSON.stringify(displayedList) !== JSON.stringify(newDisplayedList)) {
        setNewDisplayedList(displayedList)
      }
    }, [displayedList])

    useEffect(() => {
      let filtered = dropdownList.filter((tarr) => {
        return !displayedList.some((d) => d._id === tarr._id)
      })
      setfilteredTariffs(filtered)
    }, [dropdownList, newDisplayedList])

    useEffect(() => {
      const formattedTariffs = displayedList.map((tarr) => {
        return {
          ...tarr,
          origin: 'original',
        }
      })
      setCombinedTariffs(formattedTariffs)
    }, [displayedList])

    useEffect(() => {
      setDeleteTariffs([])
      setAddTariffs([])
      setNewTariffs([])
    }, [isEditing])

    const handleRemove = (_id, toBeRemoved) => {
      if (toBeRemoved) {
        const index = deleteTariffs.indexOf(_id)
        if (index === -1) {
          setDeleteTariffs([...deleteTariffs, _id])
        }
      } else {
        let index = deleteTariffs.indexOf(_id)
        if (index > -1) {
          setDeleteTariffs(deleteTariffs.filter((id) => id !== _id))
        }
      }
      const index = addTariffs.indexOf(_id)
      if (index > -1) {
        setAddTariffs(
          addTariffs.filter((id) => {
            return id !== _id
          }),
        )
      }
    }
    const handleAdd = (_id) => {
      let index = addTariffs.indexOf(_id)
      if (index === -1) {
        setAddTariffs([...addTariffs, _id])
      }
      index = dropdownList.findIndex((tarr) => tarr._id === _id)
      if (index > -1) {
        const alreadyExist = newTariffs.filter((tarr) => {
          return tarr._id === _id
        })
        if (alreadyExist.length !== 1) {
          const formattedNewTariff = {
            ...dropdownList[index],
            origin: 'new',
          }
          setCombinedTariffs((prevState) => {
            return [...prevState, formattedNewTariff]
          })
          setNewTariffs([...newTariffs, formattedNewTariff])
        }
      }

      let newFilteredTariffs = filteredTariffs.filter((tarr) => {
        return tarr._id !== _id
      })
      setfilteredTariffs(newFilteredTariffs)
    }

    const resetValues = () => {
      setDeleteTariffs([])
      setAddTariffs([])
      setNewTariffs([])
      setCombinedTariffs((prevState) => {
        return prevState.filter((tarr) => tarr.origin === 'original')
      })
    }

    const handleSubmit = (resetValuesFlag) => {
      onSubmit(addTariffs, deleteTariffs)
      if (resetValuesFlag) {
        resetValues()
      }
    }

    const handleCancel = () => {
      resetValues()
    }

    const didChange = () => {
      return deleteTariffs.length > 0 || addTariffs.length > 0
    }

    useImperativeHandle(ref, () => ({
      handleSubmit,
      handleCancel,
      didChange,
    }))
    const options = {
      scrollbars: {
        visibility: 'auto',
        autoHide: 'move',
        autoHideDelay: 1000,
      },
    }
    const rootRef = useRef(null)
    const [scroller, setScroller] = useState(null)
    const [initialize, osInstance] = useOverlayScrollbars({
      options,
      defer: true,
    })

    useEffect(() => {
      const { current: root } = rootRef
      if (scroller && root) {
        initialize({
          target: root,
          elements: {
            viewport: scroller,
          },
        })
      }
      return () => osInstance()?.destroy()
    }, [scroller, initialize, osInstance])

    return (
      <div
        className={
          isEditing ? Styles.TariffListWrapperEditing : Styles.TariffListWrapper
        }
        style={
          isEditing
            ? isLoading
              ? {}
              : {
                  height: combinedTariffs.length > 0 ? dynamicHeight : '',
                  maxHeight: maxListHeight,
                }
            : {}
        }
      >
        {displayedList?.length === 0 &&
        newTariffs.length === 0 &&
        !isLoading ? (
          <div
            className={Styles.EmptyContainer}
            style={isEditing ? { height: '4.25rem', marginTop: '0.5rem' } : {}}
          >
            <EmptySpecs title={t('ChargepointSpecs.NoTariffs')} />
          </div>
        ) : isLoading ? (
          <div style={{ height: scrollbarsHeight, overflow: 'hidden' }}>
            {[...Array(6)].map((e, index) => {
              return <ChargePointTariffListItemSkeleton key={index} />
            })}
          </div>
        ) : (
          <div
            className={Styles.ListWrapper}
            style={{ width: '100%', height: '100%' }}
          >
            <Virtuoso
              style={{ height: '100%' }}
              data={combinedTariffs}
              scrollerRef={(ref) => {
                if (ref) {
                  setScroller(ref)
                }
              }}
              increaseViewportBy={480}
              totalListHeightChanged={(height) => {
                setDynamicHeight(height + combinedTariffs.length * 8 + 40)
              }}
              itemContent={(index, tarr) => {
                const { min, max } = getEnergyPriceRange(tarr.elements)
                return (
                  <ChargePointTariffListItem
                    key={tarr?._id}
                    _id={tarr?._id}
                    name={tarr?.name}
                    elements={tarr.elements}
                    minPrice={min}
                    maxPrice={max}
                    expiryDate={
                      tarr?.expiryDate
                        ? moment(tarr?.expiryDate).format('DD-MM-YYYY HH:mm')
                        : null
                    }
                    isDefault={tarr.tariff?.isDefault}
                    power={tarr.tariff?.chargePointExpiresAfter?.power}
                    consumedPower={tarr.consumedPower}
                    isEditing={isEditing}
                    onClick={(_id, toBeRemoved) => {
                      handleRemove(_id, toBeRemoved)
                    }}
                    timeSlots={tarr?.tariff?.timeSlots}
                    country={country}
                  />
                )
              }}
            />
          </div>
        )}
        {isEditing && !isLoading && (
          <div
            className={
              removeExtraMargin
                ? Styles.ChargingSpecsAddPadding
                : Styles.ChargingSpecsAdd
            }
          >
            <ChargingSpecsAdd
              data={filteredTariffs}
              onSelect={(value) => {
                handleAdd(value)
              }}
              RenderMenuItem={ChargePointTariffListItem}
              RenderMenuItemkeys={[
                '_id',
                'name',
                'expiryDate',
                'consumedPower',
                'power',
                'minPrice',
                'maxPrice',
                'elements',
                'isEditing',
                'isDefault',
                'timeSlots',
                'country',
                'onClick',
              ]}
              infiniteLoader={false}
            />
          </div>
        )}
      </div>
    )
  },
)

TariffList.propTypes = {
  displayedList: PropTypes.array,
  dropdownList: PropTypes.array,
  isEditing: PropTypes.bool,
  isLoading: PropTypes.bool,
  maxListHeight: PropTypes.any,
  scrollbarsHeight: PropTypes.any,
  onSubmit: PropTypes.func,
  isEmpty: PropTypes.bool,
}

export default TariffList
